import { Asset, AssetState, AssetType, UUID } from '@/api/generatedApi';
import { useGetAssetThumbnail } from '@/hooks/api/assets/useGetAssetThumbnail';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetName } from '@/utils/assets';
import { isDocument, isImage, isModel, isPDFDocument } from '@/utils/models';
import PDFIcon from '@mui/icons-material/PictureAsPdf';
import TextDocumentIcon from '@mui/icons-material/TextSnippet';
import { Box, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Placeholder } from './Placeholder';

const ThumbnailRoot = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
}));

const ThumbnailContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const StyledIcon = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  '& path': {
    fill: theme.palette.text.disabled,
  },
}));

export type ThumbnailAsset = {
  id: UUID;
  tenantId?: UUID;
  path?: string;
  state?: AssetState;
  type?: AssetType;
};

const ThumbnailWrapper = ({
  asset,
  size = 72,
  children,
  noLink = false,
}: {
  asset?: ThumbnailAsset;
  size?: number;
  children: React.ReactNode;
  noLink?: boolean;
}) => {
  const { getPath, getTenantId } = useTenants();
  const assetId = asset?.id;
  const tenantId = asset?.tenantId || getTenantId();

  return noLink || getTenantId() !== tenantId || !assetId ? (
    <ThumbnailRoot style={{ width: size, height: size }}>{children}</ThumbnailRoot>
  ) : (
    <Link to={getPath(`asset/${assetId}`)} style={{ width: size, height: size, textDecoration: 'none', flexShrink: 0 }}>
      <ThumbnailRoot>{children}</ThumbnailRoot>
    </Link>
  );
};

const AssetThumbnail = ({ asset, size = 72 }: { asset: ThumbnailAsset; size?: number }) => {
  const { getTenantId } = useTenants();
  const assetId = asset.id;
  const tenantId = asset.tenantId || getTenantId();

  const { thumbnail, isLoading, error } = useGetAssetThumbnail({
    assetId,
    assetTenantId: tenantId,
  });
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [assetId, asset.state]);

  useEffect(() => {
    if (!isLoading) {
      setHasError(!!error);
    }
  }, [error, isLoading]);

  if (!thumbnail && !isLoading && !hasError) {
    return;
  }

  return (
    <Box style={{ width: size, height: size, border: '1px solid transparent' }}>
      <ThumbnailContainer style={{ width: size - 4, height: size - 4 }}>
        {!isLoading && !hasError && (
          <img
            src={thumbnail}
            alt={asset.path ? getAssetName(asset as Asset) : 'Asset'}
            width={size}
            height={size}
            style={{ objectFit: 'cover' }}
            onError={() => setHasError(true)}
          />
        )}
        {hasError && <Placeholder title='Error loading thumbnail' size={size} />}
      </ThumbnailContainer>
    </Box>
  );
};

const FileThumbnail = ({ file, size = 72 }: { file: File; size?: number }) => {
  const objectUrl = URL.createObjectURL(file);

  return (
    <Box style={{ width: size, height: size, border: '1px solid transparent' }}>
      <ThumbnailContainer>
        <img
          src={objectUrl}
          alt={file.name || 'Uploaded image'}
          width={size}
          height={size}
          style={{ objectFit: 'contain' }}
        />
      </ThumbnailContainer>
    </Box>
  );
};

const PDFThumbnail = () => {
  return (
    <StyledIcon>
      <PDFIcon style={{ width: '100%', height: '100%' }} />
    </StyledIcon>
  );
};

const DocumentThumbnail = () => {
  return (
    <StyledIcon>
      <TextDocumentIcon style={{ width: '100%', height: '100%' }} />
    </StyledIcon>
  );
};

export const Thumbnail = ({
  asset,
  file,
  size = 72,
  noLink = false,
  children,
  title,
}: {
  asset?: ThumbnailAsset;
  file?: File;
  size?: number;
  noLink?: boolean;
  children?: React.ReactNode;
  title?: string;
}) => {
  const renderContent = () => {
    if (file) {
      return <FileThumbnail file={file} size={size} />;
    }

    if (asset) {
      if (asset.type) {
        if (isModel(asset as Asset) || isImage(asset as Asset)) {
          return <AssetThumbnail asset={asset} size={size} />;
        }

        if (isPDFDocument(asset as Asset)) {
          return <PDFThumbnail />;
        }

        if (isDocument(asset as Asset)) {
          return <DocumentThumbnail />;
        }
      } else {
        return <AssetThumbnail asset={asset} size={size} />;
      }
    }

    return children || <Placeholder title={title ?? 'Asset'} size={size} />;
  };

  return (
    <ThumbnailWrapper asset={asset} size={size} noLink={noLink}>
      {renderContent()}
    </ThumbnailWrapper>
  );
};
